import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { GoogleSignInButton } from '../ui/GoogleSignInButton';
import { AppleSignInButton } from '../ui/AppleSignInButton';
import { useAuthStore } from '../../store/authStore';
import { RegisterRequest } from '../../types';
import { TimezoneSelector } from '../ui/TimezoneSelector';
import { detectUserTimezone, normalizeTimezone } from '../../utils/timezone';
import { EmailVerificationForm } from './EmailVerificationForm';
import { authApi } from '../../services/authApi';

const registerSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z
    .string()
    .min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSuccess,
  onSwitchToLogin,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedTz, setSelectedTz] = useState<string>(normalizeTimezone(detectUserTimezone()));
  const [currentStep, setCurrentStep] = useState<'register' | 'verify-email' | 'completing'>('register');
  const [pendingRegistration, setPendingRegistration] = useState<RegisterRequest | null>(null);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { register: registerUser } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setError('');
    setIsLoading(true);
    
    const { confirmPassword, ...formData } = data;
    const registerData: RegisterRequest = {
      ...formData,
      timeZone: selectedTz,
    };

    try {
      // First, send verification email
      const verificationResponse = await authApi.sendVerificationEmail({ 
        email: registerData.email 
      });
      
      if (verificationResponse.success) {
        setPendingRegistration(registerData);
        setVerificationEmail(registerData.email);
        setCurrentStep('verify-email');
      } else {
        setError(verificationResponse.message);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send verification email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailVerified = async () => {
    if (!pendingRegistration) {
      setError('Registration data not found. Please start over.');
      setCurrentStep('register');
      return;
    }

    setCurrentStep('completing');
    setIsLoading(true);

    try {
      // Complete the registration with verified email
      const success = await registerUser(pendingRegistration);
      if (success) {
        reset();
        onSuccess?.();
      } else {
        setCurrentStep('register');
        setPendingRegistration(null);
        setVerificationEmail('');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed');
      setCurrentStep('register');
      setPendingRegistration(null);
      setVerificationEmail('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToRegistration = () => {
    setCurrentStep('register');
    setPendingRegistration(null);
    setVerificationEmail('');
    setError('');
  };

  // Show completion loading state
  if (currentStep === 'completing') {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="card p-8">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Completing Registration
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Setting up your account...
            </p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show email verification form
  if (currentStep === 'verify-email') {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="card p-8">
          <EmailVerificationForm
            email={verificationEmail}
            onVerificationSuccess={handleEmailVerified}
            onBack={handleBackToRegistration}
          />
        </div>
      </div>
    );
  }

  // Show registration form
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="card p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Create Account</h1>
          <p className="text-gray-600 dark:text-gray-400">Join Koru and start building better habits</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-md p-4">
              <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              type="text"
              placeholder="John"
              leftIcon={<User size={20} />}
              error={errors.firstName?.message}
              {...register('firstName')}
            />

            <Input
              label="Last Name"
              type="text"
              placeholder="Doe"
              leftIcon={<User size={20} />}
              error={errors.lastName?.message}
              {...register('lastName')}
            />
          </div>

          <Input
            label="Email Address"
            type="email"
            placeholder="john.doe@example.com"
            leftIcon={<Mail size={20} />}
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            leftIcon={<Lock size={20} />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            }
            error={errors.password?.message}
            helperText="Must contain uppercase, lowercase, and number"
            {...register('password')}
          />

          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            leftIcon={<Lock size={20} />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            }
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <div className="grid grid-cols-1 gap-4">
            <TimezoneSelector
              label="Timezone"
              helperText="Detected from your device. You can change this anytime in your profile."
              value={selectedTz}
              onChange={setSelectedTz}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 focus:outline-none focus:underline"
            >
              Sign in here
            </button>
          </p>
        </div>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <GoogleSignInButton 
              text="signup_with"
              disabled={isLoading}
            />
            <AppleSignInButton 
              text="signup"
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="mt-6 text-xs text-gray-500 text-center">
          By creating an account, you agree to our{' '}
          <button
            type="button"
            className="text-primary-600 hover:text-primary-500 underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
            onClick={() => {
              // TODO: Navigate to Terms of Service
              console.log('Terms of Service clicked');
            }}
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <button
            type="button"
            className="text-primary-600 hover:text-primary-500 underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
            onClick={() => {
              // TODO: Navigate to Privacy Policy
              console.log('Privacy Policy clicked');
            }}
          >
            Privacy Policy
          </button>
        </div>
      </div>
    </div>
  );
}; 